import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { PackageCheck, Hammer, Boxes, TruckIcon } from "lucide-react";

const Donut = ({ pct, color, label }) => {
  const r = 42, c = 2 * Math.PI * r;
  const off = c - (Math.min(pct, 100) / 100) * c;
  return (
    <div className="flex flex-col items-center justify-center">
      <svg width="110" height="110" viewBox="0 0 110 110" className="-rotate-90">
        <circle cx="55" cy="55" r={r} fill="none" stroke="hsl(214,32%,91%)" strokeWidth="11" />
        <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="11" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <div className="-mt-[74px] mb-[34px] text-center">
        <span className="font-heading text-2xl font-black tracking-tighter">{pct}%</span>
      </div>
      <span className="text-xs text-muted-foreground font-medium">{label}</span>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, unitless, color }) => (
  <Card className="border border-border shadow-none">
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="font-heading text-2xl font-black tracking-tighter">{value}</p>
      </div>
    </CardContent>
  </Card>
);

const fmt = (n) => (n || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 });

export const MaterialConsumptionReport = () => {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [obra, setObra] = useState("all");
  const [category, setCategory] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/clients`, { withCredentials: true })
      .then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!clientId) { setData(null); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ client_id: clientId });
      if (obra !== "all") params.append("obra", obra);
      if (category !== "all") params.append("category", category);
      const { data: d } = await axios.get(`${API}/reports/material-consumption?${params}`, { withCredentials: true });
      setData(d);
    } catch { console.error("Erro ao carregar consumo"); }
    finally { setLoading(false); }
  }, [clientId, obra, category]);

  useEffect(() => { load(); }, [load]);

  const catChart = data ? data.by_category.map((c) => ({
    name: c.category, Recebido: c.received, Utilizado: c.used, Disponivel: c.available,
  })) : [];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={clientId} onValueChange={(v) => { setClientId(v); setObra("all"); setCategory("all"); }}>
          <SelectTrigger className="w-64" data-testid="consumption-client-select"><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
          <SelectContent>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_code ? `${c.client_code} - ` : ""}{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={obra} onValueChange={setObra} disabled={!data}>
          <SelectTrigger className="w-48" data-testid="consumption-obra-select"><SelectValue placeholder="Obra" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as obras</SelectItem>
            {data?.filter_options.obras.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory} disabled={!data}>
          <SelectTrigger className="w-48" data-testid="consumption-category-select"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            {data?.filter_options.categories.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!clientId ? (
        <Card className="border border-dashed border-border shadow-none">
          <CardContent className="py-16 text-center text-muted-foreground">Selecione um cliente para ver o consumo de material.</CardContent>
        </Card>
      ) : loading ? (
        <p className="text-muted-foreground text-sm">Carregando...</p>
      ) : data ? (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="border border-border shadow-none lg:col-span-1">
              <CardContent className="p-4 flex justify-around items-center">
                <Donut pct={data.totals.pct_received} color="hsl(160,60%,40%)" label="Recebido" />
                <Donut pct={data.totals.pct_falta} color="hsl(25,90%,53%)" label="Falta Receber" />
                <Donut pct={data.totals.pct_used} color="hsl(222,80%,45%)" label="Utilizado" />
              </CardContent>
            </Card>
            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
              <StatCard icon={PackageCheck} label="Recebido" value={fmt(data.totals.received)} color="bg-emerald-100 text-emerald-700" />
              <StatCard icon={Hammer} label="Utilizado" value={fmt(data.totals.used)} color="bg-blue-100 text-blue-700" />
              <StatCard icon={Boxes} label="Disponivel" value={fmt(data.totals.available)} color="bg-slate-100 text-slate-700" />
              <StatCard icon={TruckIcon} label="Falta Receber" value={fmt(data.totals.falta)} color="bg-orange-100 text-orange-700" />
            </div>
          </div>

          {/* Category chart */}
          <Card className="border border-border shadow-none">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Por Categoria</CardTitle></CardHeader>
            <CardContent>
              <div className="h-72">
                {catChart.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={catChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(214,32%,91%)" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} className="capitalize" />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmt(v)} />
                      <Legend />
                      <Bar dataKey="Recebido" fill="hsl(160,60%,40%)" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Utilizado" fill="hsl(222,80%,45%)" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="Disponivel" fill="hsl(215,16%,65%)" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-center text-muted-foreground py-16">Sem dados</p>}
              </div>
            </CardContent>
          </Card>

          {/* By obra */}
          <Card className="border border-border shadow-none">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Por Obra</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Obra</TableHead>
                      <TableHead>Recebido</TableHead>
                      <TableHead>Utilizado</TableHead>
                      <TableHead>Disponivel</TableHead>
                      <TableHead className="w-[180px]">% Utilizado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.by_obra.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Sem dados</TableCell></TableRow>
                    ) : data.by_obra.map((o) => (
                      <TableRow key={o.obra} data-testid={`consumption-obra-row-${o.obra}`}>
                        <TableCell className="font-medium text-sm">{o.obra}</TableCell>
                        <TableCell className="text-sm">{fmt(o.received)}</TableCell>
                        <TableCell className="text-sm">{fmt(o.used)}</TableCell>
                        <TableCell className="text-sm">{fmt(o.available)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                              <div className="h-full bg-blue-500" style={{ width: `${Math.min(o.pct_used, 100)}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground w-10 text-right">{o.pct_used}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Detail table */}
          <Card className="border border-border shadow-none">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Detalhamento dos Materiais</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Codigo</TableHead>
                      <TableHead>Material</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Obra</TableHead>
                      <TableHead>Recebida</TableHead>
                      <TableHead>Utilizada</TableHead>
                      <TableHead>Disponivel</TableHead>
                      <TableHead>Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.materials.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-6 text-muted-foreground">Nenhum material</TableCell></TableRow>
                    ) : data.materials.map((m) => (
                      <TableRow key={m.id} data-testid={`consumption-mat-row-${m.id}`}>
                        <TableCell className="font-mono text-xs">{m.code || "-"}</TableCell>
                        <TableCell className="text-sm font-medium">{m.name}</TableCell>
                        <TableCell className="text-sm capitalize">{m.category}</TableCell>
                        <TableCell className="text-sm">{m.obra || "-"}</TableCell>
                        <TableCell className="text-sm">{fmt(m.received)} {m.unit}</TableCell>
                        <TableCell className="text-sm">{fmt(m.used)} {m.unit}</TableCell>
                        <TableCell className="text-sm font-medium">{fmt(m.available)} {m.unit}</TableCell>
                        <TableCell className="text-sm">{m.expected ? `${fmt(m.expected)} ${m.unit}` : "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
};
